// S-02 - User Management (Admin) - US-03
import { useEffect, useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import {
  domainsApi,
  referentialsApi,
  userApi,
  type DomainDto,
  type RefItemDto,
  type UserDto,
  ApiRequestError,
} from "../api/api";
import { PageHeader } from "../components/ui/PageHeader";
import { localeLabel } from "../utils/locale";

type FormState = {
  internalId: string;
  email: string;
  fullName: string;
  role: string;
  population: string;
  workforce: "INTERNAL" | "EXTERNAL";
  language: string;
  countryId: string;
  domainIds: string[];
  departmentId: string;
  managerId: string;
  matrixManagerIds: string[];
  password: string;
  active: boolean;
};

const emptyForm: FormState = {
  internalId: "",
  email: "",
  fullName: "",
  role: "",
  population: "",
  workforce: "INTERNAL",
  language: "",
  countryId: "",
  domainIds: [],
  departmentId: "",
  managerId: "",
  matrixManagerIds: [],
  password: "",
  active: true,
};

function inferWorkforceFromEmail(email: string): "INTERNAL" | "EXTERNAL" {
  const local = email.trim().split("@")[0] ?? "";
  return local.toLowerCase().endsWith("-ext") ? "EXTERNAL" : "INTERNAL";
}

export function UserManagementPage() {
  const { t, i18n } = useTranslation();
  const [users, setUsers] = useState<UserDto[]>([]);
  const [domains, setDomains] = useState<DomainDto[]>([]);
  const [roles, setRoles] = useState<RefItemDto[]>([]);
  const [populations, setPopulations] = useState<RefItemDto[]>([]);
  const [languages, setLanguages] = useState<RefItemDto[]>([]);
  const [departments, setDepartments] = useState<RefItemDto[]>([]);
  const [countries, setCountries] = useState<RefItemDto[]>([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<FormState>(emptyForm);
  const [tempPassword, setTempPassword] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const [usersRes, domainsRes, rolesRes, popRes, langRes, deptRes, countryRes] =
        await Promise.all([
          userApi.list(q ? { q } : undefined),
          domainsApi.list(),
          referentialsApi.list("roles"),
          referentialsApi.list("populations"),
          referentialsApi.list("languages"),
          referentialsApi.list("departments"),
          referentialsApi.list("countries"),
        ]);
      setUsers(usersRes.users);
      setDomains(domainsRes.domains);
      setRoles(rolesRes.items.filter((i) => i.active));
      setPopulations(popRes.items.filter((i) => i.active));
      setLanguages(langRes.items.filter((i) => i.active));
      setDepartments(deptRes.items.filter((i) => i.active));
      setCountries(countryRes.items.filter((i) => i.active));
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.message : t("load_failed"));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
  }, []);

  function startCreate() {
    setEditingId(null);
    setForm({
      ...emptyForm,
      role: roles[0]?.code ?? "",
      population: populations[0]?.code ?? "",
      language: languages.find((l) => l.code === "FR")?.code ?? languages[0]?.code ?? "",
      countryId: countries.find((c) => c.code === "FR")?.id ?? countries[0]?.id ?? "",
    });
    setTempPassword(null);
    setShowForm(true);
  }

  function startEdit(user: UserDto) {
    setEditingId(user.id);
    setForm({
      internalId: user.internalId,
      email: user.email,
      fullName: user.fullName,
      role: user.role,
      population: user.population,
      workforce: user.workforce ?? "INTERNAL",
      language: user.language,
      countryId: user.countryId,
      domainIds: user.domainIds,
      departmentId: user.departmentId ?? "",
      managerId: user.managerId ?? "",
      matrixManagerIds: user.matrixManagerIds ?? [],
      password: "",
      active: user.active,
    });
    setTempPassword(null);
    setShowForm(true);
  }

  function toggleDomain(id: string) {
    setForm((prev) => ({
      ...prev,
      domainIds: prev.domainIds.includes(id)
        ? prev.domainIds.filter((d) => d !== id)
        : [...prev.domainIds, id],
    }));
  }

  function toggleMatrixManager(id: string) {
    setForm((prev) => ({
      ...prev,
      matrixManagerIds: prev.matrixManagerIds.includes(id)
        ? prev.matrixManagerIds.filter((m) => m !== id)
        : [...prev.matrixManagerIds, id],
    }));
  }

  // Candidats manager : comptes actifs ayant un rôle Manager ou Admin (accessLevel >= 50).
  const managerRoleCodes = new Set(
    roles
      .filter((r) => (r.accessLevel ?? 0) >= 50)
      .map((r) => r.code.toUpperCase())
  );
  const managerCandidates = users.filter(
    (u) => u.active && u.id !== editingId && managerRoleCodes.has(u.role.toUpperCase())
  );

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    setTempPassword(null);
    try {
      if (editingId) {
        await userApi.patch(editingId, {
          email: form.email,
          fullName: form.fullName,
          role: form.role,
          population: form.population,
          workforce: form.workforce,
          language: form.language,
          countryId: form.countryId,
          domainIds: form.domainIds,
          departmentId: form.departmentId || null,
          managerId: form.managerId || null,
          matrixManagerIds: form.matrixManagerIds,
          active: form.active,
        });
      } else {
        const result = await userApi.create({
          internalId: form.internalId.trim(),
          email: form.email.trim(),
          fullName: form.fullName.trim(),
          role: form.role,
          population: form.population,
          workforce: form.workforce,
          language: form.language,
          countryId: form.countryId,
          domainIds: form.domainIds,
          departmentId: form.departmentId || null,
          managerId: form.managerId || undefined,
          matrixManagerIds: form.matrixManagerIds,
          password: form.password || undefined,
        });
        if (result.temporaryPassword) setTempPassword(result.temporaryPassword);
      }
      setShowForm(false);
      await load();
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.message : t("save_failed"));
    }
  }

  async function onResetPassword(user: UserDto) {
    if (!window.confirm(t("reset_password_confirm", { id: user.internalId }))) return;
    const custom = window.prompt(t("reset_password_prompt"));
    if (custom === null) return;
    try {
      const result = await userApi.resetPassword(user.id, custom.trim() || undefined);
      if (result.temporaryPassword) {
        setTempPassword(result.temporaryPassword);
        window.alert(`${t("temp_password_once")} ${result.temporaryPassword}`);
      }
    } catch (err) {
      window.alert(err instanceof ApiRequestError ? err.message : t("reset_failed"));
    }
  }

  return (
    <div className="sm-page">
      <PageHeader
        title={t("user_management")}
        subtitle={t("users_sub")}
        actions={
          <>
            <Link to="/users/import" className="sm-btn sm-btn-secondary">
              {t("import_xlsx")}
            </Link>
            <button type="button" className="sm-btn sm-btn-primary" onClick={startCreate}>
              {t("create_user")}
            </button>
          </>
        }
      />

      <div className="sm-toolbar" style={{ marginBottom: 16 }}>
        <input
          className="sm-input"
          placeholder={t("search_users")}
          value={q}
          onChange={(e) => setQ(e.target.value)}
          style={{ maxWidth: 320 }}
        />
        <button type="button" className="sm-btn sm-btn-primary" onClick={() => void load()}>
          {t("filter")}
        </button>
      </div>

      {tempPassword ? (
        <div className="sm-alert sm-alert-info" role="status">
          {t("temp_password_once")} <strong>{tempPassword}</strong>
        </div>
      ) : null}

      {error ? (
        <div className="sm-alert sm-alert-error" role="alert">
          {error}
        </div>
      ) : null}

      {showForm ? (
        <form onSubmit={onSubmit} className="sm-form-card">
          <h2>{editingId ? t("edit_user") : t("create_user")}</h2>
          {!editingId ? (
            <label className="sm-label">
              {t("matricule")}
              <input
                required
                className="sm-input"
                value={form.internalId}
                onChange={(e) => setForm({ ...form, internalId: e.target.value })}
              />
            </label>
          ) : null}
          <label className="sm-label">
            {t("full_name")}
            <input
              required
              className="sm-input"
              value={form.fullName}
              onChange={(e) => setForm({ ...form, fullName: e.target.value })}
            />
          </label>
          <label className="sm-label">
            {t("email_label")}
            <input
              required
              type="email"
              className="sm-input"
              value={form.email}
              onChange={(e) => {
                const email = e.target.value;
                setForm({
                  ...form,
                  email,
                  workforce: inferWorkforceFromEmail(email),
                });
              }}
            />
          </label>
          <div className="sm-grid-3">
            <label className="sm-label">
              {t("role")}
              <select
                required
                className="sm-select"
                value={form.role}
                onChange={(e) => setForm({ ...form, role: e.target.value })}
              >
                {roles.map((r) => (
                  <option key={r.id} value={r.code}>
                    {localeLabel(i18n.language, r)} ({r.code})
                  </option>
                ))}
              </select>
            </label>
            <label className="sm-label">
              {t("population")}
              <select
                required
                className="sm-select"
                value={form.population}
                onChange={(e) => setForm({ ...form, population: e.target.value })}
              >
                {populations.map((p) => (
                  <option key={p.id} value={p.code}>
                    {localeLabel(i18n.language, p)} ({p.code})
                  </option>
                ))}
              </select>
            </label>
            <label className="sm-label">
              {t("workforce")}
              <select
                required
                className="sm-select"
                value={form.workforce}
                onChange={(e) =>
                  setForm({
                    ...form,
                    workforce: e.target.value as "INTERNAL" | "EXTERNAL",
                  })
                }
              >
                <option value="INTERNAL">{t("workforce_internal_opt")}</option>
                <option value="EXTERNAL">{t("workforce_external_opt")}</option>
              </select>
            </label>
          </div>
          <div className="sm-grid-3">
            <label className="sm-label">
              {t("language")}
              <select
                required
                className="sm-select"
                value={form.language}
                onChange={(e) => setForm({ ...form, language: e.target.value })}
              >
                {languages.map((l) => (
                  <option key={l.id} value={l.code}>
                    {localeLabel(i18n.language, l)} ({l.code})
                  </option>
                ))}
              </select>
            </label>
            <label className="sm-label">
              {t("country")}
              <select
                required
                className="sm-select"
                value={form.countryId}
                onChange={(e) => setForm({ ...form, countryId: e.target.value })}
              >
                <option value="">—</option>
                {countries.map((c) => (
                  <option key={c.id} value={c.id}>
                    {localeLabel(i18n.language, c)} ({c.code})
                  </option>
                ))}
              </select>
            </label>
            <label className="sm-label">
              {t("department")}
              <select
                className="sm-select"
                value={form.departmentId}
                onChange={(e) => setForm({ ...form, departmentId: e.target.value })}
              >
                <option value="">—</option>
                {departments.map((d) => (
                  <option key={d.id} value={d.id}>
                    {localeLabel(i18n.language, d)} ({d.code})
                  </option>
                ))}
              </select>
            </label>
          </div>
          <fieldset className="sm-fieldset">
            <legend>{t("domain")}</legend>
            <div className="sm-check-row">
              {domains.map((d) => (
                <label key={d.id}>
                  <input
                    type="checkbox"
                    checked={form.domainIds.includes(d.id)}
                    onChange={() => toggleDomain(d.id)}
                  />{" "}
                  {d.code} — {localeLabel(i18n.language, d)}
                </label>
              ))}
            </div>
          </fieldset>
          <label className="sm-label">
            {t("manager")}
            <select
              className="sm-select"
              value={form.managerId}
              onChange={(e) => setForm({ ...form, managerId: e.target.value })}
            >
              <option value="">—</option>
              {managerCandidates.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.fullName} ({m.internalId})
                </option>
              ))}
            </select>
          </label>
          <fieldset className="sm-fieldset">
            <legend>{t("matrix_managers")}</legend>
            <p className="sm-muted" style={{ margin: "0 0 8px" }}>
              {t("matrix_managers_hint")}
            </p>
            <div className="sm-check-row">
              {managerCandidates
                .filter((m) => m.id !== form.managerId)
                .map((m) => (
                  <label key={m.id}>
                    <input
                      type="checkbox"
                      checked={form.matrixManagerIds.includes(m.id)}
                      onChange={() => toggleMatrixManager(m.id)}
                    />{" "}
                    {m.fullName} ({m.internalId})
                  </label>
                ))}
            </div>
          </fieldset>
          {!editingId ? (
            <label className="sm-label">
              {t("password_optional")}
              <input
                type="password"
                className="sm-input"
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                minLength={12}
              />
            </label>
          ) : (
            <label className="sm-check-row">
              <input
                type="checkbox"
                checked={form.active}
                onChange={(e) => setForm({ ...form, active: e.target.checked })}
              />
              {t("account_active")}
            </label>
          )}
          <div className="sm-form-actions">
            <button type="submit" className="sm-btn sm-btn-primary">
              {t("save")}
            </button>
            <button type="button" className="sm-btn sm-btn-ghost" onClick={() => setShowForm(false)}>
              {t("cancel")}
            </button>
          </div>
        </form>
      ) : null}

      {loading ? (
        <p className="sm-muted">{t("loading")}</p>
      ) : (
        <div className="sm-card">
          <div className="sm-table-wrap">
          <table className="sm-table">
            <thead>
              <tr>
                <th>{t("full_name")}</th>
                <th>{t("matricule")}</th>
                <th>{t("email_label")}</th>
                <th>{t("role")}</th>
                <th>{t("population")}</th>
                <th>{t("workforce")}</th>
                <th>{t("country")}</th>
                <th>{t("domain")}</th>
                <th>{t("manager")}</th>
                <th>{t("status")}</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id}>
                  <td style={{ fontWeight: 500 }}>{u.fullName}</td>
                  <td style={{ fontFamily: "ui-monospace, Consolas, monospace", fontSize: 12 }}>
                    {u.internalId}
                  </td>
                  <td style={{ fontSize: 12 }}>{u.email}</td>
                  <td>
                    <span className="sm-badge-pill sm-badge-blue">{u.role}</span>
                  </td>
                  <td>
                    <span className="sm-badge-pill sm-badge-gray">{u.population || "—"}</span>
                  </td>
                  <td>
                    <span
                      className={`sm-badge-pill ${
                        u.workforce === "EXTERNAL" ? "sm-badge-yellow" : "sm-badge-green"
                      }`}
                    >
                      {u.workforce === "EXTERNAL"
                        ? t("workforce_external_opt")
                        : t("workforce_internal_opt")}
                    </span>
                  </td>
                  <td>
                    {u.countryCode || "—"}
                    {u.countryPlaque ? ` · ${u.countryPlaque}` : ""}
                  </td>
                  <td style={{ fontSize: 12 }}>{u.domains.join(", ") || "—"}</td>
                  <td style={{ fontSize: 12 }}>
                    {u.managerName || "—"}
                    {u.matrixManagers.length
                      ? ` (+${u.matrixManagers.length} ${t("matrix_short")})`
                      : ""}
                  </td>
                  <td>
                    <span className={`sm-badge-pill ${u.active ? "sm-badge-green" : "sm-badge-red"}`}>
                      {u.active ? t("status_active") : t("status_inactive")}
                    </span>
                  </td>
                  <td>
                    <div className="actions">
                      <button type="button" className="sm-link" onClick={() => startEdit(u)}>
                        {t("edit")}
                      </button>
                      <button type="button" className="sm-link" onClick={() => void onResetPassword(u)}>
                        {t("reset_password")}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </div>
      )}
    </div>
  );
}
